import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useSubscription } from "@/hooks/useSubscription";
import { PlanBadge } from "@/components/PlanBadge";
import { AlertCircle, Plus, Edit, Trash2, QrCode, Loader2, ExternalLink, BarChart3, FileText, Mail, Lock, Shield, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { exportProfileToPDF } from "@/utils/pdfExport";
import { EmailPreview } from "@/components/EmailPreview";

interface Profile {
  id: string;
  first_name: string;
  last_name: string;
  photo_url: string;
  profile_image_url?: string;
  created_at: string;
}

const Dashboard = () => {
  const navigate = useNavigate();
  const { subscription, loading: subLoading, isPremium, isPro } = useSubscription();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [profileLimit, setProfileLimit] = useState<number | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [exportingPDF, setExportingPDF] = useState<string | null>(null);
  const [sendingEmail, setSendingEmail] = useState<string | null>(null);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [userEmail, setUserEmail] = useState<string>("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    checkAuthAndLoadProfiles();
  }, []);

  const checkAuthAndLoadProfiles = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      toast.error("Bitte melden Sie sich an");
      navigate("/auth");
      return;
    }

    setUserEmail(session.user.email || "");
    await loadProfiles(session.user.id);
    await determineProfileLimit(session.user.id);
  };

  const loadProfiles = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, photo_url, profile_image_url, created_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setProfiles(data || []);
    } catch (error) {
      console.error("Error loading profiles:", error);
      toast.error("Fehler beim Laden der Profile");
    } finally {
      setLoading(false);
    }
  };

  const determineProfileLimit = async (userId: string) => {
    try {
      const { data: subData } = await supabase
        .from("subscriptions")
        .select("plan_type")
        .eq("user_id", userId)
        .maybeSingle();

      if (!subData || subData.plan_type === "free") {
        setProfileLimit(1);
      } else if (subData.plan_type === "premium") {
        setProfileLimit(2);
      } else if (subData.plan_type === "pro") {
        setProfileLimit(5);
      } else {
        setProfileLimit(1); // Default fallback
      }
    } catch (error) {
      console.error("Error determining profile limit:", error);
      setProfileLimit(1); // Default to 1 on error
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from("profiles")
        .delete()
        .eq("id", id);

      if (error) throw error;

      setProfiles(profiles.filter(p => p.id !== id));
      toast.success("Profil erfolgreich gelöscht");
      setDeleteId(null);
    } catch (error) {
      console.error("Error deleting profile:", error);
      toast.error("Fehler beim Löschen des Profils");
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-portal-session");

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error: any) {
      console.error("Error opening portal:", error);
      toast.error("Fehler beim Öffnen des Kundenportals: " + error.message);
    } finally {
      setPortalLoading(false);
    }
  };

  const handleExportPDF = async (profileId: string) => {
    if (!isPremium && !isPro) {
      toast.error("PDF-Export ist nur für Premium und Pro Mitglieder verfügbar");
      return;
    }

    setExportingPDF(profileId);
    try {
      // Fetch profile data
      const { data: profileData, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", profileId)
        .single();

      if (error || !profileData) {
        throw new Error("Fehler beim Laden der Profildaten");
      }

      // Generate QR code
      const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(
        `${window.location.origin}/profile/${profileId}`
      )}`;

      const response = await fetch(qrCodeUrl);
      const blob = await response.blob();
      const qrCodeDataUrl = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });

      // Export to PDF
      const parseMeds = (data: any) => Array.isArray(data) ? data.map((m: any) => typeof m === 'string' ? {name: m} : m) : [];
      const parseAllergies = (data: any) => Array.isArray(data) ? data.map((a: any) => typeof a === 'string' ? {name: a} : a) : [];
      const parseConds = (data: any) => Array.isArray(data) ? data.map((c: any) => typeof c === 'string' ? {name: c} : c) : [];

      await exportProfileToPDF(
        {
          ...profileData,
          emergency_contacts: profileData.emergency_contacts as Array<{ name: string; phone: string; relation: string }>,
          medications: parseMeds(profileData.medications),
          allergies: parseAllergies(profileData.allergies),
          conditions: parseConds(profileData.conditions),
        },
        qrCodeDataUrl
      );
      toast.success("PDF erfolgreich erstellt!");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Fehler beim Erstellen des PDFs");
    } finally {
      setExportingPDF(null);
    }
  };

  const handleResendConfirmation = async (profileId: string) => {
    if (!isPremium && !isPro) {
      toast.error("Bestätigungs-E-Mails sind nur für Premium und Pro Mitglieder verfügbar");
      return;
    }

    setSendingEmail(profileId);
    try {
      const { error } = await supabase.functions.invoke("send-confirmation-email", {
        body: { profileId },
      });

      if (error) throw error;

      toast.success("Bestätigungs-E-Mail wurde versendet!");
    } catch (error) {
      console.error("Error sending confirmation email:", error);
      toast.error("Fehler beim Versenden der E-Mail");
    } finally {
      setSendingEmail(null);
    }
  };

  const handleSendTestEmail = async () => {
    setSendingTestEmail(true);
    try {
      const { error } = await supabase.functions.invoke("send-test-email", {
        body: {},
      });

      if (error) throw error;

      toast.success("Test-E-Mail wurde erfolgreich an Robin@r-Dieker.de versendet!");
    } catch (error) {
      console.error("Error sending test email:", error);
      toast.error("Fehler beim Senden der Test-Email");
    } finally {
      setSendingTestEmail(false);
    }
  };

  const canCreateMore = profileLimit === null || profiles.length < profileLimit;

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("Die Passwörter stimmen nicht überein");
      return;
    }

    if (newPassword.length < 10) {
      toast.error("Das neue Passwort muss mindestens 10 Zeichen lang sein");
      return;
    }

    // Validate password strength
    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumber = /[0-9]/.test(newPassword);
    const hasSpecialChar = /[^A-Za-z0-9]/.test(newPassword);

    if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar) {
      toast.error("Das Passwort muss Groß- und Kleinbuchstaben, Zahlen und Sonderzeichen enthalten");
      return;
    }

    setChangingPassword(true);

    try {
      // First verify current password by attempting to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: userEmail,
        password: currentPassword,
      });

      if (signInError) {
        toast.error("Aktuelles Passwort ist falsch");
        setChangingPassword(false);
        return;
      }

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) throw updateError;

      toast.success("Passwort erfolgreich geändert");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      console.error("Error changing password:", error);
      toast.error("Fehler beim Ändern des Passworts: " + error.message);
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading || subLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Wird geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 py-12 px-4">
      <div className="container mx-auto max-w-6xl">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Zurück
          </Button>
        </Link>

        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">
            Mein <span className="gradient-text">Dashboard</span>
          </h1>
          <p className="text-muted-foreground">
            Verwalten Sie Ihre Notfallprofile und Ihr Abonnement
          </p>
        </div>

        {/* Subscription Status Card */}
        <Card className="mb-8 glass">
          <CardHeader>
            <CardTitle>Abonnement-Status</CardTitle>
            <CardDescription>Ihr aktueller Plan und verfügbare Features</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Aktueller Plan</p>
                <PlanBadge plan={subscription?.plan_type || "free"} />
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Status</p>
                <p className="text-sm font-medium capitalize">{subscription?.status || "active"}</p>
              </div>
              {subscription?.current_period_end && (
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Gültig bis</p>
                  <p className="text-sm font-medium">
                    {new Date(subscription.current_period_end).toLocaleDateString("de-DE")}
                  </p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-4 border-t">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Profile erstellt</p>
                <p className="text-lg font-semibold">
                  {profiles.length} {profileLimit !== null ? `/ ${profileLimit}` : ""}
                  {profileLimit === null && " (Unbegrenzt)"}
                </p>
              </div>
              <div className="flex gap-2 flex-wrap">
                {(isPremium || isPro) && (
                  <>
                    <Button
                      onClick={() => navigate("/statistics")}
                      variant="outline"
                    >
                      <BarChart3 className="mr-2 h-4 w-4" />
                      Statistiken
                    </Button>
                    <EmailPreview
                      firstName={profiles[0]?.first_name || "Max"}
                      lastName={profiles[0]?.last_name || "Mustermann"}
                      planType={subscription?.plan_type || "free"}
                    />
                    {userEmail === "robin@r-dieker.de" && (
                      <Button
                        onClick={handleSendTestEmail}
                        variant="outline"
                        disabled={sendingTestEmail}
                      >
                        {sendingTestEmail ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Mail className="mr-2 h-4 w-4" />
                        )}
                        Test-Email senden
                      </Button>
                    )}
                    <Button
                      onClick={handleManageSubscription}
                      disabled={portalLoading}
                      variant="outline"
                    >
                      {portalLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Öffnet...
                        </>
                      ) : (
                        <>
                          <ExternalLink className="mr-2 h-4 w-4" />
                          Abo verwalten
                        </>
                      )}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Security Card - Password Change */}
        <Card className="mb-8 glass">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              <CardTitle>Sicherheit & Passwort</CardTitle>
            </div>
            <CardDescription>
              Ändern Sie Ihr Passwort, um Ihr Konto zu schützen
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current-password">Aktuelles Passwort</Label>
                <Input
                  id="current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  placeholder="Geben Sie Ihr aktuelles Passwort ein"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="new-password">Neues Passwort</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  placeholder="Mindestens 10 Zeichen"
                />
                <p className="text-xs text-muted-foreground">
                  Muss Groß- und Kleinbuchstaben, Zahlen und Sonderzeichen enthalten
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Neues Passwort bestätigen</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Passwort wiederholen"
                />
              </div>

              <Button 
                type="submit" 
                disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
                className="w-full"
              >
                <Lock className="mr-2 h-4 w-4" />
                {changingPassword ? "Wird geändert..." : "Passwort ändern"}
              </Button>
            </form>

            <div className="mt-6 p-4 bg-primary/5 rounded-lg border border-primary/20">
              <div className="flex gap-3">
                <AlertCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div className="space-y-2">
                  <p className="text-sm font-medium">Passwort vergessen?</p>
                  <p className="text-xs text-muted-foreground">
                    Aus Sicherheitsgründen für Ihre sensiblen Gesundheitsdaten erfolgt die Passwort-Zurücksetzung 
                    ausschließlich über unseren Support. Dies gewährleistet maximale Sicherheit und verhindert 
                    unbefugten Zugriff auf Ihre medizinischen Informationen.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => window.location.href = 'mailto:R.Dieker@DiekerIT.com?subject=MediQR - Passwort zurücksetzen&body=Hallo,%0D%0A%0D%0AIch möchte mein Passwort zurücksetzen.%0D%0A%0D%0AMeine registrierte E-Mail-Adresse: ' + userEmail}
                  >
                    <Mail className="mr-2 h-3 w-3" />
                    Support kontaktieren
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Profiles Section */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Meine Profile</h2>
              <p className="text-muted-foreground">
                {profiles.length === 0 
                  ? "Sie haben noch keine Profile erstellt" 
                  : `${profiles.length} ${profiles.length === 1 ? "Profil" : "Profile"} verfügbar`}
              </p>
            </div>
            <Button
              onClick={() => navigate("/create")}
              disabled={!canCreateMore}
            >
              <Plus className="mr-2 h-4 w-4" />
              Neues Profil
            </Button>
          </div>

          {!canCreateMore && (
            <Card className="bg-muted/50 border-muted">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">
                  Sie haben Ihr Profil-Limit erreicht. 
                  <button 
                    onClick={() => navigate("/pricing")}
                    className="text-primary hover:underline ml-1"
                  >
                    Upgraden Sie jetzt
                  </button>, um weitere Profile zu erstellen.
                </p>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {profiles.map((profile) => (
              <Card key={profile.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <div className="aspect-square relative">
                  <img
                    src={profile.photo_url}
                    alt={`${profile.first_name} ${profile.last_name}`}
                    className="w-full h-full object-cover"
                  />
                  {profile.profile_image_url && (
                    <div className="absolute top-2 right-2">
                      <img
                        src={profile.profile_image_url}
                        alt="Profil-Kennzeichnung"
                        className="w-12 h-12 rounded-lg object-cover border-2 border-white shadow-lg"
                      />
                    </div>
                  )}
                </div>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {profile.first_name} {profile.last_name}
                  </CardTitle>
                  <CardDescription>
                    Erstellt am {new Date(profile.created_at).toLocaleDateString("de-DE")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate(`/qr/${profile.id}`)}
                  >
                    <QrCode className="mr-2 h-4 w-4" />
                    QR-Code anzeigen
                  </Button>
                  {(isPremium || isPro) && (
                    <>
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => handleExportPDF(profile.id)}
                        disabled={exportingPDF === profile.id}
                      >
                        {exportingPDF === profile.id ? (
                          <>
                            <FileText className="mr-2 h-4 w-4 animate-pulse" />
                            PDF wird erstellt...
                          </>
                        ) : (
                          <>
                            <FileText className="mr-2 h-4 w-4" />
                            Als PDF exportieren
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => handleResendConfirmation(profile.id)}
                        disabled={sendingEmail === profile.id}
                      >
                        {sendingEmail === profile.id ? (
                          <>
                            <Mail className="mr-2 h-4 w-4 animate-pulse" />
                            E-Mail wird versendet...
                          </>
                        ) : (
                          <>
                            <Mail className="mr-2 h-4 w-4" />
                            Bestätigung erneut senden
                          </>
                        )}
                      </Button>
                    </>
                  )}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => navigate(`/create?edit=${profile.id}`)}
                    >
                      <Edit className="mr-2 h-4 w-4" />
                      Bearbeiten
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setDeleteId(profile.id)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Löschen
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {profiles.length === 0 && (
            <Card className="py-12">
              <CardContent className="text-center space-y-4">
                <div className="text-muted-foreground">
                  <p className="text-lg">Noch keine Profile vorhanden</p>
                  <p className="text-sm">Erstellen Sie Ihr erstes Notfallprofil</p>
                </div>
                <Button onClick={() => navigate("/create")}>
                  <Plus className="mr-2 h-4 w-4" />
                  Erstes Profil erstellen
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteId !== null} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Profil löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Diese Aktion kann nicht rückgängig gemacht werden. Das Profil und der zugehörige QR-Code werden dauerhaft gelöscht.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction onClick={() => deleteId && handleDelete(deleteId)}>
                Löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default Dashboard;
